import moment from 'moment';
import { groupBy, isBoolean, isEmpty, isNil, isNumber } from 'lodash';

import { __t, applyTranslations } from 'utils/language';
import { CHANNELS_WITH_METRICS } from 'utils/channels';
import { getItem, removeItem, setItem } from 'utils/storage/localStorage';
import { getSelectedValues } from 'modules/discovery/utils/discovery';

import type {
  DataStructureCommon,
  DiscoveryCreatorsCategories,
  DiscoveryCreatorsCategoriesFormatted,
  DiscoveryCreatorsContentTypes,
  DiscoveryCreatorsFilterAudienceFormatted,
  DiscoveryCreatorsFilterKeywords,
  DiscoveryCreatorsFilterKeywordsFormatted,
  DiscoveryCreatorsFilters,
  DiscoveryCreatorsQueryObject,
  DiscoveryCreatorsShareSection,
  DiscoveryCreatorsTaxonomyCategory,
  DiscoveryCreatorsTaxonomyGroup,
  InfluencerTile as InfluencerTileType,
  MixedSelectDropdownItem
} from 'types';

type StringNumber = string | number;

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('../components/sort/discovery-creators-sort.i18n.json'));

export const DEBOUNCE_DELAY = 500;
export const TAXONOMY_BLACKLIST = ['pets', 'skills'];
export const DEFAULT_SORT_OPTION = 'joined.desc';
export const DEFAULT_CHANNEL = 'instagram';
export const SAVE_COLLECTION_MAX_NUM_RESULTS = 2000;

const sortMsgKey = 'discovery_creators_sort';

export const SORT_LIST = {
  'age.desc': __t(`${sortMsgKey}.sort.age.desc`),
  'age.asc': __t(`${sortMsgKey}.sort.age.asc`),
  'engagement.desc': __t(`${sortMsgKey}.sort.engagement.desc`),
  'engagement.asc': __t(`${sortMsgKey}.sort.engagement.asc`),
  'followers.desc': __t(`${sortMsgKey}.sort.followers_subscribers.desc`),
  'followers.asc': __t(`${sortMsgKey}.sort.followers_subscribers.asc`),
  'joined.desc': __t(`${sortMsgKey}.sort.joined.desc`),
  'joined.asc': __t(`${sortMsgKey}.sort.joined.asc`),
  'price.desc': __t(`${sortMsgKey}.sort.price.desc`),
  'price.asc': __t(`${sortMsgKey}.sort.price.asc`),
  'relevance.desc': __t(`${sortMsgKey}.sort.relevance.desc`),
  'country.desc': __t(`${sortMsgKey}.sort.country.desc`),
  'artist.asc': __t(`${sortMsgKey}.sort.artist.asc`),
  'instagramusername.asc': __t(`${sortMsgKey}.sort.instagramusername.asc`),
  'tiktokusername.asc': __t(`${sortMsgKey}.sort.tiktokusername.asc`),
  'youtubeusername.asc': __t(`${sortMsgKey}.sort.youtubeusername.asc`)
};

// note: these are endpoint prop names, not local prop names
export const SEARCH_BY_NAME_PROP_LIST = [
  'audience_cities',
  'channels',
  'cities',
  'countries',
  'gender',
  'min_age',
  'max_age',
  'name',
  'unverified',
  'verified'
];

// note: these are local prop names, not endpoint prop names
const RELEVANCE_SORT_PROPS = [
  'audienceCountries',
  'audienceCountriesTikTok',
  'categories',
  'countries',
  'keywords',
  'searchByName'
];

export const SORT_BY_USERNAME_VALUES = [
  'instagramusername.asc',
  'tiktokusername.asc',
  'youtubeusername.asc'
];

// note: these are endpoint prop names, not local prop names
export const UNVERIFIED_ALLOWED_PROPS = [
  'audience_ages',
  'audience_ages_share',
  'audience_cities',
  'audience_countries',
  'audience_countries_share',
  'audience_credibility',
  'audience_interests',
  'audience_reachability',
  // tiktok
  'min_avg_views_tiktok',
  'max_avg_views_tiktok',
  'min_engagement_tiktok',
  'max_engagement_tiktok',
  'min_followers_tiktok',
  'max_followers_tiktok',
  // tiktok audience
  'audience_ages_tiktok',
  'audience_ages_share_tiktok',
  'audience_countries_tiktok',
  'audience_countries_share_tiktok',
  'audience_reachability_tiktok',
  'min_audience_gender_female_ratio_tiktok',
  'max_audience_gender_female_ratio_tiktok',
  'min_audience_gender_male_ratio_tiktok',
  'max_audience_gender_male_ratio_tiktok',
  // tiktok ^
  'channels',
  'cities',
  'countries',
  'email_required',
  'excluded_keywords',
  'gender',
  'followers_tiers',
  'included_keywords',
  'included_keywords_operator',
  'min_age',
  'max_age',
  'min_avg_views',
  'max_avg_views',
  'min_engagement_instagram',
  'max_engagement_instagram',
  'min_engagement_youtube',
  'max_engagement_youtube',
  'min_followers_instagram',
  'max_followers_instagram',
  'min_followers_youtube',
  'max_followers_youtube',
  'min_audience_credibility',
  'max_audience_credibility',
  'min_audience_gender_female_ratio',
  'max_audience_gender_female_ratio',
  'min_audience_gender_male_ratio',
  'max_audience_gender_male_ratio',
  'name',
  'specialized_in',
  'specialized_in_operator',
  'fashion_styles',
  'unverified',
  'verified'
];

// note: these are local prop names, not endpoint prop names
export const UNVERIFIED_DISABLED_PROPS = [
  'bodyAttributes',
  'ethnicGroups',
  'professions'
];

const COUNTRY_SORT_PROPS = [
  'audienceCountries',
  'audienceCountriesTikTok'
];

export const LIGHTWEIGHT_CHECKBOX_STORAGE_NAME = 'lightweight-checkbox';
export const SEARCH_BY_NAME_STORAGE_NAME = 'search-by-name';
export const HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME = 'has-manually-selected-channel';

export const setStorageValue = (key: string, value: string): void => {
  setItem(key, value);
};

export const handleStorageValue = (key: string): boolean => {
  const storageDate = getItem(key);
  const now = Date.now();

  if (isNil(storageDate)) {
    setStorageValue(key, String(now));
    return true;
  }

  const storageDateNumber = Number(storageDate);
  const numDaysSinceStorage = moment(now).diff(moment(storageDateNumber), 'days');

  if (numDaysSinceStorage >= 1) {
    removeItem(key);
    setStorageValue(key, String(now));

    return true;
  }

  return false;
};

export const isChannelChecked = (channels: string, channel: string): boolean => (
  channels.includes(channel)
);

export const shouldDefaultFiltersBeSent = (channels: string, channel: string): boolean => (
  isEmpty(channels)
  || channels.includes(channel)
  || (!channels.includes('instagram') && !channels.includes('youtube') && !channels.includes('tiktok'))
);

export const getFollowerTiers = (data: Array<DataStructureCommon>): string => (
  data.filter(t => t.selected).map(t => t.value).join(',')
);

export const getSelectedGenders = (genders: Array<DataStructureCommon>): string => {
  const selectedGenders = genders.filter(g => g.selected);

  return selectedGenders.map(g => g.value)
    .join(',');
};

export const getSelectedAudienceShare = (
  filters: DiscoveryCreatorsFilters,
  key: DiscoveryCreatorsShareSection
): string | undefined | null => {
  const shareObject = filters[key];
  const value = shareObject.value;
  const isSelected = shareObject.checkbox.selected;

  // ignore the checkbox state unless the value is valid
  if (isSelected && Number(value) > 0) {
    return value;
  }

  return null;
};

export const getSelectedAudience = (filters: DiscoveryCreatorsFilters): DiscoveryCreatorsFilterAudienceFormatted => {
  const ages = filters.audienceAges.filter(a => a.selected);
  const cities = getSelectedValues(filters.audienceCities);
  const countries = getSelectedValues(filters.audienceCountries);
  const credibility = filters.audienceCredibility.filter(a => a.selected);
  const interests = filters.audienceInterests.filter(entry => entry.isSelected);
  const reachability = filters.audienceReachability.filter(a => a.selected);

  const genderRatio = {
    female: {
      min: filters.audienceGenderRatio.female.min / 100,
      max: filters.audienceGenderRatio.female.max / 100
    },
    male: {
      min: filters.audienceGenderRatio.male.min / 100,
      max: filters.audienceGenderRatio.male.max / 100
    }
  };

  return {
    ages: ages.map(a => a.value).join(','),
    cities: cities.join(','),
    countries: countries.join(','),
    credibility: credibility.map(c => c.value).join(','),
    genderRatio,
    interests: interests.map(entry => entry.value).join(','),
    reachability: reachability.map(c => c.value).join(',')
  };
};

export const getSelectedAudienceTikTok = (
  filters: DiscoveryCreatorsFilters
): Partial<DiscoveryCreatorsFilterAudienceFormatted> => {
  const ages = filters.audienceAgesTikTok.filter(a => a.selected);
  const countries = getSelectedValues(filters.audienceCountriesTikTok);
  const reachability = filters.audienceReachabilityTikTok.filter(a => a.selected);

  const genderRatio = {
    female: {
      min: filters.audienceGenderRatioTikTok.female.min / 100,
      max: filters.audienceGenderRatioTikTok.female.max / 100
    },
    male: {
      min: filters.audienceGenderRatioTikTok.male.min / 100,
      max: filters.audienceGenderRatioTikTok.male.max / 100
    }
  };

  return {
    ages: ages.map(a => a.value).join(','),
    countries: countries.join(','),
    genderRatio,
    reachability: reachability.map(c => c.value).join(',')
  };
};


export const getArrayOfSelectedChannels = (channels: Array<DataStructureCommon>): Array<string> => {
  const selectedChannels = channels.filter(c => c.selected);

  return selectedChannels.map(c => c.identifier);
};

export const getSelectedChannels = (channels: Array<DataStructureCommon>): string => (
  getArrayOfSelectedChannels(channels).join(',')
);

export const getKeywords = (
  keywords: DiscoveryCreatorsFilterKeywords
): DiscoveryCreatorsFilterKeywordsFormatted => ({
  included: keywords.included.map(encodeURIComponent).join(','),
  excluded: keywords.excluded.map(encodeURIComponent).join(','),
  matchMode: keywords.matchMode
});

export const getSelectedTaxonomyIds = (
  items: Array<MixedSelectDropdownItem>
): Array<StringNumber | undefined | null> => (
  items.filter(item => item.isSelected).map(item => item.id)
);

export const getCategories = (
  categories: DiscoveryCreatorsCategories
): DiscoveryCreatorsCategoriesFormatted => ({
  items: getSelectedTaxonomyIds(categories.items),
  matchMode: categories.matchMode
});

const prepareTaxonomies = (
  taxonomies: Array<DiscoveryCreatorsTaxonomyGroup>,
  type: DiscoveryCreatorsContentTypes
): Array<DiscoveryCreatorsTaxonomyCategory> | undefined | null => {
  const items = taxonomies.find(item => item.identifier === type);

  if (isNil(items)) {
    return null;
  }

  return items.elements;
};

export const getCategoriesFilter = (
  taxonomies: Array<MixedSelectDropdownItem>,
  matchMode: 'and' | 'or'
): DiscoveryCreatorsCategories => ({
  items: taxonomies,
  matchMode
});

export const getTaxonomies = (
  taxonomies: Array<DiscoveryCreatorsTaxonomyGroup>,
  type: DiscoveryCreatorsContentTypes,
  blacklist: Array<string> = []
): Array<MixedSelectDropdownItem> => {
  const preparedTaxonomies = prepareTaxonomies(taxonomies, type);
  const filteredTaxonomies = (preparedTaxonomies || []).filter(taxonomy => !blacklist.includes(taxonomy.label));

  return (filteredTaxonomies || []).map(category => ({
    id: category.id,
    identifier: category.label,
    value: category.label,
    label: __t(`collabary_common.${type}.${category.label}`),
    isDisabled: false,
    isFocused: false,
    isSelected: false
  }));
};

export const getCreatorIdsGroupedByMetricChannels = (
  creators: Array<InfluencerTileType>
): { [x: string]: Array<number> } => {
  const creatorIdsWithAvailableChannel = creators.map((creator) => {
    const creatorChannels = Object.keys(creator.channels)
      .filter(channel => Boolean(creator.channels[channel]));
    const creatorChannelsWithMetrics = CHANNELS_WITH_METRICS
      .filter(channel => creatorChannels.includes(channel));
    const channelToFetch = creatorChannelsWithMetrics[0];

    return {
      id: creator.id,
      channel: channelToFetch
    };
  });

  const result = {};
  const groupedCreators = groupBy(creatorIdsWithAvailableChannel, 'channel');

  Object.keys(groupedCreators)
    .forEach((channel) => {
      if (!CHANNELS_WITH_METRICS.includes(channel)) {
        // skip undefined values
        return;
      }
      result[channel] = groupedCreators[channel].map(creator => creator.id);
    });

  return result;
};

export const getUpdatedTileSize = (currentTileSize: string): string => (
  currentTileSize === 'collapsed' ? 'expanded' : 'collapsed'
);

export const getTileSize = (type: string): string => (
  getItem(`influencer_tile_${type}`) || 'expanded'
);

export const setTileSize = (type: string, updatedValue?: string): void => {
  const name = `influencer_tile_${type}`;
  const currentValue = getTileSize(type);

  setItem(name, updatedValue || getUpdatedTileSize(currentValue));
};

export const getChannel = (filterChannels: Array<DataStructureCommon> | undefined): string => {
  const selectedChannels = (filterChannels || []).filter(channel => channel.selected);
  const instagram = selectedChannels.some(channel => channel.identifier === 'instagram');
  const youtube = selectedChannels.some(channel => channel.identifier === 'youtube');
  const tiktok = selectedChannels.some(channel => channel.identifier === 'tiktok');

  if (tiktok && !youtube && !instagram) {
    return 'tiktok';
  }

  if (youtube && !instagram) {
    return 'youtube';
  }

  return 'instagram';
};

export const hasTaxonomyItems = (
  categories: Array<MixedSelectDropdownItem>
): boolean => {
  const categoryIds = getSelectedTaxonomyIds(categories);

  return !isEmpty(categoryIds);
};

export const hasKeywords = (keywords: DiscoveryCreatorsFilterKeywords): boolean => (
  !isEmpty(keywords.included) || !isEmpty(keywords.excluded)
);

export const isRelevanceSortSelected = (filters: DiscoveryCreatorsFilters): boolean => (
  hasTaxonomyItems(filters.categories.items)
  || hasKeywords(filters.keywords)
  || !isEmpty(filters.searchByName)
);

export const isCountriesSortSelected = (filters: DiscoveryCreatorsFilters): boolean => (
  !isEmpty(filters.countries) || !isEmpty(filters.audienceCountries) || !isEmpty(filters.audienceCountriesTikTok)
);

const getChannelFromSortParam = (value: string) => (
  value.replace('username.asc', '')
);

export const isChannelSortSelected = (
  value: string,
  selectedChannels: string
) => {
  const channel = getChannelFromSortParam(value);

  return selectedChannels.includes(channel);
};

export const getSortValue = (
  filters: DiscoveryCreatorsFilters,
  filterKey: string,
  currentSortValue: string
): string => {
  if (isRelevanceSortSelected(filters)) {
    return 'relevance.desc';
  }

  if (isCountriesSortSelected(filters)) {
    return COUNTRY_SORT_PROPS.includes(filterKey) ? 'country.desc' : currentSortValue;
  }

  if (filterKey === 'channels' && SORT_BY_USERNAME_VALUES.includes(currentSortValue)) {
    const selectedChannels = getSelectedChannels(filters.channels);

    return isChannelSortSelected(currentSortValue, selectedChannels) ? currentSortValue : DEFAULT_SORT_OPTION;
  }

  return RELEVANCE_SORT_PROPS.includes(filterKey)
    ? DEFAULT_SORT_OPTION
    : currentSortValue;
};

export const addDefaultInstagramFilters = (query: string): string => (
  `${query}is_active=true&is_private=false&`
);

export const convertObjectToString = (queryObject: Partial<DiscoveryCreatorsQueryObject>): string => {
  const keys = Object.keys(queryObject);

  const queryArray = keys.reduce<Array<string>>((arr, next) => {
    const value = queryObject[next];

    return isNumber(value) || isBoolean(value) || !isEmpty(value)
      ? arr.concat([`${next}=${value}`])
      : arr;
  }, []);

  return queryArray.join('&');
};
